import { useSortable } from "@dnd-kit/sortable";
import { motion, useAnimate } from "framer-motion";
import { ANIMATION_DURATIONS, CARD_CORRECTIONS } from "../../utils/consts";
import AnimatedCard from "../AnimatedCard/AnimatedCard";
import { forwardRef, useEffect, useImperativeHandle } from "react";

// Card default state
const initialTransform = {
  x: 0,
  y: 0,
  zIndex: 0,
  visibility: "visible",
};

const DraggableBox = forwardRef(function DraggableBox(props, ref) {
  const {
    item, // Data given to element
    onClick, // onClick callback, has item as paramater
    children, // html content
    checkLock, // Lock interactions when showing correction animations
    idx, // actual order
    onStage, // should be popping visible or not
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
    isSorting,
  } = useSortable({
    id: item.id,
    // Disable dnd-kit's layout transition
    transition: null,

    // Un-grabbable: disable while checking or if card is already checked ok
    disabled: item.locked || checkLock,
    // Un-movable: null == not move, undefined == use default
    strategy: item.locked ? () => null : undefined,
  });

  const [thisCard, animateCard] = useAnimate();

  // TODO: move logic to handleCheck
  const correction = item.locked ? CARD_CORRECTIONS.locked : item.correction;

  // Return item to callback
  function handleClick(evt) {
    evt.stopPropagation();
    onClick?.(item ?? null);
  }

  useImperativeHandle(ref, () => ({
    scrollHere: (behavior) => {
      thisCard.current?.scrollIntoView({
        behavior: behavior,
        block: "nearest",
        inline: "nearest",
      });
    },
    needsScroll: () => {
      return correction === CARD_CORRECTIONS.ok;
    }
  }));

  // OnMount + onStage == false
  useEffect(() => {
    if (onStage) {
      setTimeout(() => {
        animateCard(
          thisCard.current,
          { scale: 1 },
          { duration: ANIMATION_DURATIONS.cardSpawn }
        );
      }, idx * ANIMATION_DURATIONS.cardSpawn_ms);
    } else {
      animateCard(
        thisCard.current,
        { scale: 0 },
        { duration: ANIMATION_DURATIONS.cardSpawn }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onStage]);

  return (
    <motion.div
      ref={(node) => {
        setNodeRef(node);
        thisCard.current = node;
      }}
      layoutId={item.id}
      initial={{ scale: 0 }}
      animate={
        transform // != null when dnd going on
          ? {
              x: transform.x,
              y: transform.y,
              zIndex: item.active || isDragging ? 1 : 0,
              visibility: isDragging ? "hidden" : "visible",
            }
          : initialTransform
      }
      transition={{
        duration: !isSorting || !isDragging ? 0.25 : 0,
      }}
      {...attributes}
      {...listeners}
      onClick={handleClick}
    >
      <AnimatedCard
        locked={item.locked}
        active={item.active}
        correction={correction}
        checkLock={checkLock || !onStage}
        isDragging={isDragging}
        onClick={onClick}
        index={idx}
      >
        {children}
      </AnimatedCard>
    </motion.div>
  );
});

export default DraggableBox;
